import {
  faChevronLeft,
  faChevronRight,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import moment from 'moment-timezone';

import WorkspaceList from './components/workspace-list/workspace-list.jsx';
import CustomButton from '../components/common/custom-button/custom-button.jsx';
import CustomSelect from '../components/common/custom-select/custom-select.jsx';
import CustomTextInputField from '../components/common/custom-text-input-field/custom-text-input-field.jsx';
import Text from '../components/common/custom-text/custom-text.jsx';
import { timezones } from '../constants/time-constants.js';
import { isValidWorkspaceName } from '../utils/validation-utils.js';
import usePaginatedWorkspaces  from '../hooks/usePaginatedWorkspaces.jsx';
import { createWorkspace, deleteWorkspace } from '../api/workspace-service.js';
import { useCurrentUser } from '../store/current-user-context.jsx';

import '../styles/signup/signup.scss';

const SignupWorkspaces = ({ formData = {}, dispatch, trackEventOnce }) => {
  const { plan } = formData;
  const [workspaceName, setWorkspaceName] = React.useState('');
  const [newWorkspaceIDs, setNewWorkspaceIDs] = React.useState([]);
  const [timezone, setTimezone] = React.useState(moment.tz.guess() || "America/New_York");
  const [creatingWorkspace, setCreatingWorkspace] = React.useState(false);
  const [deletingWorkspaceIDs, setDeletingWorkspaceIDs] = React.useState([]);
  const validWorkspaceName = isValidWorkspaceName(workspaceName);
  const { user } = useCurrentUser();
  const { agencyId } = user || {};
  const { workspaces, loading, refresh, totalCount } = usePaginatedWorkspaces(0, 100, agencyId);

  React.useEffect(() => {
    dispatch({
      type: 'UPDATE_WORKSPACE_COUNT',
      payload: totalCount,
    });
  }, [totalCount, dispatch]);

  const handlePreviousStep = () => {
    dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: 1 });
  };

  const handleNextStep = () => {
    trackEventOnce('registration_step3', {
      agency_id: agencyId,
      plan: plan.name,
    });
    
    dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: 3 });
  };

  const handleAddWorkspace = event => {
    event.preventDefault();
    setCreatingWorkspace(true);

    if (!validWorkspaceName) {
      return;
    }

    const trimmedWorkspaceName = workspaceName.trim();
    const newWorkspace = {
      workspaceName: trimmedWorkspaceName,
      timezone: timezone,
    };

    createWorkspace({ workspace: newWorkspace })
      .then((response) => {
        const { brand } = response.data;
        const { id: brandId } = brand;
        setNewWorkspaceIDs(prevIDs => [brandId, ...prevIDs]);
        refresh();
      })
      .catch(error => {
        console.error('Error creating workspace:', error);
      })
      .finally(() => {
        setWorkspaceName('');
        setCreatingWorkspace(false);
      });
  };

  const handleDeleteWorkspace = (workspace) => {
    if (deletingWorkspaceIDs.includes(workspace.id)) {
      return;
    }

    setDeletingWorkspaceIDs(prevIDs => [...prevIDs, workspace.id]);

    deleteWorkspace({ workspaceId: workspace.id })
      .then(() => {
        setNewWorkspaceIDs(prevIDs => prevIDs.filter(id => id !== workspace.id));
        refresh();
      })
      .catch(error => {
        console.error('Error deleting workspace:', error);
      })
      .finally(() => {
        setDeletingWorkspaceIDs(prevIDs => prevIDs.filter(id => id !== workspace.id));
      });
  };

  const sortedWorkspaces = React.useMemo(() => {
    const idSet = new Set(newWorkspaceIDs);

    const newItems = newWorkspaceIDs
      .map(id => workspaces.find(ws => ws.id === id))
      .filter(Boolean);

      const remaining = workspaces
      .filter(ws => !idSet.has(ws.id))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );

      return [...newItems, ...remaining];
  }, [workspaces, newWorkspaceIDs]);

  return (
    <div className="register-container">
      <div className="register-form">
        <section className="signup-form">
          <Text as="h2" variant="l1" color="#2B4A78">
            Plan: {plan.label}
          </Text>
          <div className="workspaces-form-header">
            <Text as="h4" variant="h4" color="#2B4A78">
              Introduction to Workspaces
            </Text>
          </div>
          <Text variant="b2" color="#2B4A78">
            A workspace is a home for your client's social media accounts, team,
            content, and more - all neatly organized and siloed from your other
            clients.
          </Text>
          <div className="workspaces-form-subheader">
            <Text as="h5" variant="h5" color="#2B4A78">
              Create More Workspaces
            </Text>
          </div>
          <Text variant="b2" color="#2B4A78">
            Do you manage Clients, Multiple Business Locations or have a
            Personal Account you want to keep separate? Create another workspace
            for that content!
          </Text>
          <form className="workspaces-form" onSubmit={handleAddWorkspace}>
            <div className="add-new-workspace">
              <CustomTextInputField
                label="Workspace Name"
                hideLabel={true}
                value={workspaceName}
                onChange={event => {
                  setWorkspaceName(event.target.value);
                }}
                placeholder="Workspace Name"
                fullWidth
              />
              <CustomSelect
                description={null}
                optional={false}
                options={timezones}
                styles={{ minWidth: '200px' }}
                value={timezone}
                onChange={event => {
                  setTimezone(event.value);
                }}
              />
            </div>
            <div className="new-workspace-actions">
              <CustomButton
                type="submit"
                variant="tertiary"
                faIcon={faPlus}
                size="small"
                disabled={!validWorkspaceName}
                isLoading={creatingWorkspace}
              >
                Add Workspace
              </CustomButton>
            </div>
          </form>
          <hr className="form-divider" />
          <WorkspaceList
            workspaces={sortedWorkspaces}
            onDelete={handleDeleteWorkspace}
            loading={loading}
            deletingWorkspaceIDs={deletingWorkspaceIDs}
          />
          <div className="signup-next-step-container">
            <CustomButton
              variant="secondary"
              faIcon={faChevronLeft}
              onClick={() => handlePreviousStep()}
            >
              Back
            </CustomButton>
            <CustomButton
              variant="primary"
              faIcon={faChevronRight}
              iconPosition="right"
              onClick={() => handleNextStep()}
              disabled={!workspaces.length}
            >
              Next Step
            </CustomButton>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SignupWorkspaces;
